/**
 * NewMeetingDialog — smart wrapper for the "Quick Start" new-meeting modal.
 *
 * Owns the React Query graph (settings + series), the startBrowserMeeting API
 * call, the sessionStorage handoff to RecordingControls, and post-start
 * navigation. The dumb view at ./NewMeetingDialogView holds every piece of
 * UI state — subject, language selection, profile / mode / series, the More-
 * options disclosure — and emits a single onStart payload when the user
 * commits.
 *
 * Offline branching also lives here: when navigator.onLine is false, we mint
 * a provisional meeting id locally and let RecordingControls promote it to a
 * real meeting on the backend once connectivity returns.
 */

import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import { useNetworkStatus } from "../../hooks/useNetworkStatus"
import { usePermissionState } from "../../hooks/usePermissionState"
import { fetchSeries, fetchSettings, startBrowserMeeting } from "../../lib/api"
import { queryKeys } from "../../lib/query-keys"
import { useProduct } from "../../lib/tenant"
import type { IProps, IStartPayload } from "./interfaces"
import { NewMeetingDialogView } from "./NewMeetingDialogView"

export function NewMeetingDialog({ open, onClose }: IProps): React.JSX.Element {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const product = useProduct()
  const { i18n } = useLingui()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isOnline } = useNetworkStatus()
  const micPermission = usePermissionState("microphone")

  // Pull company settings — primary use is to seed `default_language` into the
  // language picker. Cached by react-query so subsequent opens are instant.
  const settingsQuery = useCompanyQuery({
    key: queryKeys.companySettingsForNewMeeting,
    queryFn: (client) => fetchSettings(client),
    enabled: !!user && open,
    staleTime: 5 * 60_000,
  })

  const seriesQuery = useCompanyQuery({
    key: queryKeys.series,
    queryFn: (client) => fetchSeries(client),
    enabled: open,
  })

  const startMutation = useMutation({
    mutationFn: async ({ subject, profile, seriesId, config }: IStartPayload): Promise<void> => {
      // Only skip the pre-record screen when we already know the mic is allowed.
      // Prompt, denied, and unknown keep the two-step check so the user hears their level first.
      const storedConfig = { ...config, autoStart: micPermission === "granted" }
      // Offline path: mint a provisional id locally and hand off to
      // RecordingControls, which will create the server-side meeting once
      // connectivity returns.
      if (!isOnline) {
        const provisionalId = `provisional_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
        sessionStorage.setItem(
          `provisional:${provisionalId}`,
          JSON.stringify({ subject, templateId: profile, seriesId }),
        )
        sessionStorage.setItem(`meeting-config:${provisionalId}`, JSON.stringify(storedConfig))
        onClose()
        navigate({ to: "/meetings/$id", params: { id: provisionalId } })
        return
      }

      if (!company) {
        throw new Error(i18n._(msg`No active workspace. Please select an organisation and try again.`))
      }

      const { id } = (await startBrowserMeeting(company, subject, profile, undefined, seriesId ?? undefined)) as {
        id: string
      }
      sessionStorage.setItem(`meeting-config:${id}`, JSON.stringify(storedConfig))
      onClose()
      navigate({ to: "/meetings/$id", params: { id } })
    },
    onSuccess: () => {
      // Refresh the meeting list so the new meeting shows up when the user
      // navigates back from the recording page. Prefix-invalidates every
      // meetings list variant (hidden filter, action-item summary) for the org.
      queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
    },
  })

  const handleStart = useCallback(
    async (payload: IStartPayload): Promise<void> => {
      await startMutation.mutateAsync(payload)
    },
    [startMutation],
  )

  const defaultLanguageSetting = settingsQuery.data
    ? ((settingsQuery.data as Record<string, string>).default_language ?? "")
    : undefined

  return (
    <NewMeetingDialogView
      open={open}
      onClose={onClose}
      isOnline={isOnline}
      product={product}
      defaultLanguageSetting={defaultLanguageSetting}
      seriesList={seriesQuery.data ?? []}
      isStarting={startMutation.isPending}
      onStart={handleStart}
    />
  )
}
